import React, { useState, useEffect, useRef, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Dialog, Snackbar } from "@mui/material";
import "./qualifying.scss";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import {
  fetchCandidateQualifyQuestions,
  getPreScreenDetails,
  submitCandidateQualifyAnswers,
} from "../../Redux/ActionCreators/qualifyQuestionsAction";

export default function QualifyQuestions() {
  const { preScreenId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [screenLoading, setScreenLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // processingState: null | 'evaluating' | 'eligible' | 'done'
  const [processingState, setProcessingState] = useState(null);
  const [evaluationMessage, setEvaluationMessage] = useState("");
  const [nextStageId, setNextStageId] = useState(null);
  const [nextStageEventType, setNextStageEventType] = useState(null);
  const [nextStageLink, setNextStageLink] = useState(null);
  const [nextStageDynamicVoiceCall, setNextStageDynamicVoiceCall] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [unanswered, setUnanswered] = useState({});

  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 10;
  const awaitingNotificationRef = useRef(false);

  const job = useSelector((state) => state.jobs.latestJob);
  const qualifyQuestions = useSelector(
    (state) => state.qualifyQuestions?.candidateQualifyQuestions
  );
  const qualifyingDetails = useSelector(
    (state) => state.qualifyQuestions?.preQualifyingDetails
  );

  const QUESTION_TYPE_ID_TO_LABEL = {
    "4c524f2f-48de-493e-894f-4f0bd931b4c9": "MCQ",
    "64468292-d741-4e52-893f-0846dd56830d": "Numeric",
    "4ffc711b-2f10-470b-b162-85f2a2f1e220": "Yes/No",
    "30afb159-85d7-490d-afcd-1af9497531ae": "Free Text",
  };

  useEffect(() => {
    const fetchPreScreen = async () => {
      setScreenLoading(true);
      try {
        await dispatch(getPreScreenDetails(preScreenId));
      } catch (error) {
        const status = error?.parsedStatus || error?.response?.status;
        if (status === 403 || status === 410) setLinkExpired(true);
        console.error("Error loading pre-screen details:", error);
      }
      setScreenLoading(false);
    };

    fetchPreScreen();
  }, [preScreenId, dispatch]);

  useEffect(() => {
    const fetchJobAndQuestions = async () => {
      if (!qualifyingDetails?.job_id || !qualifyingDetails?.job_candidate_id) return;

      setDetailsLoading(true);
      try {
        await dispatch(fetchJobById(qualifyingDetails.job_id));
        await dispatch(
          fetchCandidateQualifyQuestions(qualifyingDetails.job_candidate_id, preScreenId)
        );
      } catch (error) {
        console.error("Error loading job or questions:", error);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchJobAndQuestions();
  }, [qualifyingDetails, preScreenId, dispatch]);

  useEffect(() => {
    const jobCandidateId = qualifyingDetails?.job_candidate_id;
    const baseUrl = process.env.REACT_APP_WS_URL;

    if (!jobCandidateId || !baseUrl) return;

    intentionalCloseRef.current = false;

    const connect = () => {
      if (intentionalCloseRef.current) return;

      const wsUrl = `${baseUrl}?job_candidate_id=${encodeURIComponent(jobCandidateId)}`;
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.type === "prescreen.evaluation.completed" && parsed.data) {
            const data = parsed.data;
            if (data.is_eligible_for_next_stage) {
              setEvaluationMessage(data.message || "");
              awaitingNotificationRef.current = true;
              // stay in 'evaluating' until the notification event arrives with stage.id
            } else {
              setProcessingState("done");
            }
          }
          if (parsed.type === "notification" && parsed.data && awaitingNotificationRef.current) {
            const stageId = parsed.data?.meta_data?.job_candidate_hiring_id;
            const eventType = parsed.data?.event_type;
            const eligibleEventTypes = [
              "telephonic_screening.assigned",
              "ai_assessments.scheduled",
              "video_interview.scheduled",
            ];
            if (stageId && eligibleEventTypes.includes(eventType)) {
              awaitingNotificationRef.current = false;
              setNextStageId(stageId);
              setNextStageEventType(eventType);
              if (eventType === "ai_assessments.scheduled") {
                setNextStageLink(parsed.data?.meta_data?.data?.assessment?.assessment_link || null);
              } else if (eventType === "video_interview.scheduled") {
                setNextStageLink(parsed.data?.meta_data?.data?.video_call?.video_call_id || null);
              } else if (eventType === "telephonic_screening.assigned") {
                setNextStageDynamicVoiceCall(
                  parsed.data?.meta_data?.data?.stage?.dynamic_voice_call || false
                );
              }
              setProcessingState("eligible");
            }
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      socket.onclose = () => {
        if (intentionalCloseRef.current) return;
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.warn("WebSocket: max reconnect attempts reached");
          return;
        }
        const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current));
        console.warn(
          `WebSocket disconnected. Reconnecting in ${delay / 1000}s… (attempt ${reconnectAttemptsRef.current + 1})`
        );
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        socket.close();
      };
    };

    connect();

    return () => {
      intentionalCloseRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (socketRef.current) socketRef.current.close();
    };
  }, [qualifyingDetails?.job_candidate_id]);

  const snackClosed = () => {
    setSuccessMessage(false);
    setSuccessMessageText("");
  };

  const handleOptionChange = (questionId, optionId, answerText = "") => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        question_id: questionId,
        selected_option_id: optionId || null,
        answer_text: answerText || "",
        score: 0,
      },
    }));
    setUnanswered((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  const submitAnswers = async () => {
    let allAnswered = true;
      let newUnanswered = {};

      for (let q of qualifyQuestions) {
        const ans = answers[q.id];
        const type = QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];

        if (type === "MCQ" || type === "Yes/No") {
          if (!ans || !ans.selected_option_id) {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        } else if (type === "Numeric") {
          if (!ans || ans.answer_text.trim() === "") {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        } else {
          if (
            !ans ||
            (!ans.selected_option_id && ans.answer_text.trim() === "")
          ) {
            allAnswered = false;
            newUnanswered[q.id] = "This question is required";
          }
        }
      }

      setUnanswered(newUnanswered);

      if (!allAnswered) {
        setSnackbarSeverity("error");
        setSuccessMessageText("Please answer all the pre quaifying questions");
        setSuccessMessage(true);
        return;
      }

      const answersArray = qualifyQuestions.map((q) => {
        const ans = answers[q.id];
        const type = QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];

        if (type === "MCQ" || type === "Yes/No") {
          return {
            question_id: q.id,
            selected_option_id: ans?.selected_option_id || null,
            score: 0,
          };
        }

        if (type === "Numeric") {
          return {
            question_id: q.id,
            answer_text: ans?.answer_text || "",
            score: 0,
          };
        }

        return {
          question_id: q.id,
          selected_option_id: ans?.selected_option_id || null,
          answer_text: ans?.answer_text || "",
          score: 0,
        };
      });

      setLoading(true);
      const success = await dispatch(
        submitCandidateQualifyAnswers(
          qualifyingDetails.job_candidate_id,
          preScreenId,
          answersArray
        )
      );
      setLoading(false);

      if (success) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        awaitingNotificationRef.current = false;
        setNextStageDynamicVoiceCall(false);
        setProcessingState("evaluating");
      } else {
        setSnackbarSeverity("error");
        setSuccessMessageText("Failed to submit answers");
        setSuccessMessage(true);
      }
  };

  if (screenLoading || detailsLoading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Dialog
        className="schedule-loader"
        open={loading}
        onClose={() => {}}
        PaperProps={{
          sx: {
            boxShadow: "none",
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {successMessageText}
        </Alert>
      </Snackbar>
      {linkExpired ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "24px",
            color: "#ff0000",
          }}
        >
          Link has been expired
        </div>
      ) : (
        <div className="d-flex">
          <div className="candidate-upload pre-screening">
            <div>
              <div className="pre-screening-heading">
                <div className="d-flex justify-content-center">
                  <img
                    src="/images/gappeoai-logo.png"
                    alt=""
                    width={262}
                    height={56.5}
                  />
                </div>
                <h3>Job Prescreening</h3>
              </div>
              {processingState === "evaluating" && (
                <div className="thank-you-screen">
                  <div className="d-flex justify-content-center">
                    <div className="spinner-border evaluation-spinner" role="status" style={{color: '#F75891'}}>
                      <span className="visually-hidden">Evaluating...</span>
                    </div>
                  </div>
                  <h2>Evaluating your responses...</h2>
                  <p>Please wait while we process your answers.</p>
                </div>
              )}
              {processingState === "eligible" && (
                <div className="thank-you-screen">
                  <div className="d-flex justify-content-center"><img src="/images/thankyou-icon.gif" alt="" /></div>
                  <p className="evaluation-message">{evaluationMessage}</p>
                  <p>Please click on continue to proceed to the next stage.</p>
                  {nextStageEventType === "telephonic_screening.assigned" && nextStageDynamicVoiceCall && (
                    <p>You will receive an OTP on your registered email address.</p>
                  )}
                  <div className="d-flex justify-content-center">
                    <button
                      className="close-btn"
                      onClick={() => {
                        if (nextStageEventType === "ai_assessments.scheduled" && nextStageLink) {
                          window.location.href = nextStageLink;
                        } else if (nextStageEventType === "video_interview.scheduled" && nextStageLink) {
                          navigate(`/video-call/${nextStageLink}`);
                        } else if (
                          nextStageEventType === "telephonic_screening.assigned" &&
                          nextStageDynamicVoiceCall
                        ) {
                          navigate(`/dynamic-voice-call/${nextStageId}`);
                        } else {
                          navigate(`/hiring-stage/${nextStageId}`);
                        }
                      }}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}
              {processingState === "done" && (
                <div className="thank-you-screen">
                  <div className="d-flex justify-content-center"><img src="/images/thankyou-icon.gif" alt="" /></div>
                  <h2>Prescreening completed</h2>
                  <p>You will receive an email notifcation for the next steps</p>
                  <div className="d-flex justify-content-center"><button className="close-btn" onClick={() => window.close()}>Close Window</button></div>
                </div>
              )}
              {!processingState && (
                <>
                  <div className="candidate-upload-header">
                    <div className="candidate-upload-title">
                      <div className="position-details">
                        <div className="d-flex justify-content-between">
                          <h3>{job?.title}</h3>
                          <button onClick={() => setShowJobDetails((prev) => !prev)}>
                            {showJobDetails ? "Hide job details" : "View job details"}
                          </button>
                        </div>
                        <div className="job-details">
                          <p>Job location: {job?.city?.name}</p>
                          <p>Company: {job?.company_name}</p>
                        </div>
                        <div className="job-details">
                          <p>Experience: {job?.experience_required}</p>
                        </div>
                        <div className="requirements">
                          {job?.skills?.slice(0, 2).map((skills, index) => (
                            <p className="candidate-skills" key={index}>
                              {skills}
                            </p>
                          ))}
                          {job?.skills?.length > 2 && (
                            <p className="candidate-skills more-skills">
                              +{job?.skills.length - 2} more
                              <div className="skills-popup">
                                {job?.skills.slice(2).map((s, idx, arr) => (
                                  <span key={idx}>
                                    {s}
                                    {idx < arr.length - 1 && ", "}
                                  </span>
                                ))}
                              </div>
                            </p>
                          )}
                          {job?.salary && (
                            <p
                              className="candidate-skills"
                              style={{
                                background: "#DEF7EC",
                                color: "#03543F",
                                width: "unset",
                                border: "1px solid #4CC948",
                              }}
                            >
                              {job?.salary}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="candidate-upload-body">
                    <div className="questions-form">
                      <h4>Answer the below questions</h4>
                    </div>
                    <div className="job-details-body">
                      <div className="question-section">
                        {qualifyQuestions.length > 0 && (
                          <div className="qualifying-question-list">
                            {qualifyQuestions.map((q, index) => {
                              const type =
                                QUESTION_TYPE_ID_TO_LABEL[q.question_type_id];
                              return (
                                <div
                                  className="qualifying-question-item"
                                  key={q.id}
                                >
                                  <h5>{index + 1}. {q.question_text}</h5>

                                  {(() => {
                                    switch (type) {
                                      case "MCQ":
                                        return (
                                          <>
                                            <select
                                              className="answer-select"
                                              value={
                                                answers[q.id]?.selected_option_id ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const selectedOption =
                                                  q.options.find(
                                                    (opt) =>
                                                      opt.id === e.target.value
                                                  );
                                                handleOptionChange(
                                                  q.id,
                                                  selectedOption?.id,
                                                  selectedOption?.option_text
                                                );
                                              }}
                                            >
                                              <option value="">
                                                Select option
                                              </option>
                                              {q.options.map((opt) => (
                                                <option key={opt.id} value={opt.id}>
                                                  {opt.option_text}
                                                </option>
                                              ))}
                                            </select>
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Numeric":
                                        return (
                                          <>
                                            <input
                                              className="qualifying-question-number-input"
                                              type="number"
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                              placeholder="Enter number"
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Yes/No":
                                        return (
                                          <>
                                            <div className="yes-no-buttons">
                                              {q.options.map((opt) => (
                                                <button
                                                  key={opt.id}
                                                  type="button"
                                                  className={`yes-no-btn ${
                                                    answers[q.id]
                                                      ?.selected_option_id ===
                                                    opt.id
                                                      ? "active"
                                                      : ""
                                                  }`}
                                                  onClick={() =>
                                                    handleOptionChange(
                                                      q.id,
                                                      opt.id,
                                                      opt.option_text
                                                    )
                                                  }
                                                >
                                                  {opt.option_text}
                                                </button>
                                              ))}
                                            </div>
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      case "Free Text":
                                        return (
                                          <>
                                            <input
                                              className="qualifying-question-text-input"
                                              type="text"
                                              maxLength={50}
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              placeholder="Enter your response"
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );

                                      default:
                                        return (
                                          <>
                                            <textarea
                                              placeholder="Enter your response"
                                              value={
                                                answers[q.id]?.answer_text || ""
                                              }
                                              onChange={(e) =>
                                                handleOptionChange(
                                                  q.id,
                                                  null,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            {unanswered[q.id] && <p className="error-text">{unanswered[q.id]}</p>}
                                          </>
                                        );
                                    }
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="qualifying-question-btn">
                    <button onClick={submitAnswers}>Submit</button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={`pre-screening-job-details ${showJobDetails ? "open" : ""}`}>
            <h3>{job?.title}</h3>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Company details</h6>
                <p>{job?.company_name}</p>
              </div>
              <div>
                <h6>Employment type</h6>
                <p>{job?.job_type?.name}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Salary range</h6>
                <p>{job?.salary}</p>
              </div>
              <div>
                <h6>Location</h6>
                <p>{job?.country?.name}, {job?.city?.name}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Experience</h6>
                <p>{job?.experience_required}</p>
              </div>
            </div>
            <div className="d-flex justify-content-between" style={{marginBottom:'32px'}}>
              <div>
                <h6>Skills</h6>
                <p>{job?.skills?.map((skills, index) => (
                    <p key={index}>{skills},</p>
                  ))}
                </p>
              </div>
            </div>
            <div>
              <div>
                <h6>Description</h6>
                <p className="description-text">{job?.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
